import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QRCodeSVG } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QrCode, Camera, CheckCircle2, X, User, GraduationCap, Clock, Building2, Users, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { categoryLabel } from "@/lib/constants";

export default function Attendance() {
  const { user } = useAuth();
  if (user.role === "student") return <StudentScan />;
  return <LecturerQR />;
}

/* ---------- Lecturer: generate QR ---------- */
function LecturerQR() {
  const [lectures, setLectures] = useState(null);
  const [selected, setSelected] = useState("");
  const [qr, setQr] = useState(null);

  useEffect(() => { api.get("/lectures").then((r) => setLectures(r.data)).catch(() => setLectures([])); }, []);

  const generate = async (id) => {
    setSelected(id);
    if (!id) return setQr(null);
    try { const r = await api.get(`/lectures/${id}/qr`); setQr(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (lectures === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="الحضور عبر QR" subtitle="أنشئ رمز QR لمحاضرة ليقوم الطلاب بمسحه وتسجيل حضورهم" />
      {lectures.length === 0 ? <EmptyState icon={QrCode} title="لا توجد محاضرات" desc="أضف محاضرة أولًا لإنشاء رمز الحضور." /> : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
            <Label>اختر المحاضرة</Label>
            <Select value={selected} onValueChange={generate}>
              <SelectTrigger data-testid="qr-lecture-select" className="mt-2 rounded-xl"><SelectValue placeholder="اختر محاضرة" /></SelectTrigger>
              <SelectContent>{lectures.map((l) => <SelectItem key={l.id} value={l.id}>{l.title} — {l.date}</SelectItem>)}</SelectContent>
            </Select>
            {qr && (
              <div className="mt-6 space-y-2 text-sm">
                <Row icon={GraduationCap} label="المحاضرة" value={qr.title} />
                <Row icon={User} label="المحاضر" value={qr.lecturer_name} />
                <Row icon={Clock} label="الوقت" value={`${qr.date} — ${qr.start_time} إلى ${qr.end_time}`} />
                <Row icon={Building2} label="الوحدة" value={qr.unit_name || "—"} />
                <Row icon={Users} label="الفئة" value={categoryLabel[qr.gender]} />
              </div>
            )}
          </div>
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
            {qr ? (
              <>
                <div className="rounded-2xl bg-white p-5 shadow-inner ring-4 ring-navy/5" data-testid="qr-code">
                  <QRCodeSVG value={qr.token} size={220} fgColor="#0F172A" level="M" />
                </div>
                <p className="mt-4 text-center text-sm text-slate-400">يعرض الطالب كاميرته لمسح هذا الرمز</p>
                <p className="mt-1 text-center text-xs text-slate-300">الرمز صالح فقط ضمن وقت المحاضرة</p>
              </>
            ) : (
              <div className="text-center text-slate-300">
                <QrCode className="mx-auto h-24 w-24" />
                <p className="mt-3 text-sm">اختر محاضرة لعرض رمز QR</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ---------- Student: scan QR ---------- */
function StudentScan() {
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [details, setDetails] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const scannerRef = useRef(null);

  const handleToken = async (token) => {
    try {
      const r = await api.post("/attendance/scan", { token });
      setDetails(r.data);
      setName(r.data.student_name || "");
      toast.success("تم قراءة الرمز");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startScan = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 250 },
          async (decoded) => { await stopScan(); await handleToken(decoded); },
          () => {});
      } catch (e) {
        toast.error("تعذّر تشغيل الكاميرا، استخدم الإدخال اليدوي");
        setScanning(false);
      }
    }, 200);
  };

  const stopScan = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); scannerRef.current = null; } } catch (e) {}
    setScanning(false);
  };

  useEffect(() => () => { stopScan(); }, []);

  const confirm = async () => {
    if (!name.trim()) { toast.error("يرجى إدخال اسمك لتأكيد الحضور"); return; }
    setSaving(true);
    try { await api.post("/attendance/confirm", { token: details.token, student_name: name.trim() }); toast.success("تم تسجيل الحضور بنجاح"); setDetails(null); setManual(""); setName(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="تسجيل الحضور" subtitle="امسح رمز QR الذي يعرضه المحاضر لتسجيل حضورك" />
      <div className="mx-auto max-w-lg">
        {!details ? (
          <div className="rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
            {scanning ? (
              <div>
                <div id="qr-reader" className="overflow-hidden rounded-xl" style={{ width: "100%" }} />
                <Button data-testid="stop-scan-btn" onClick={stopScan} variant="outline" className="mt-4 w-full rounded-xl border-red-200 text-red-500"><X className="me-2 h-4 w-4" /> إيقاف المسح</Button>
              </div>
            ) : (
              <div className="text-center">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-2xl bg-navy/5 text-navy"><Camera className="h-12 w-12" /></div>
                <Button data-testid="start-scan-btn" onClick={startScan} className="mt-5 w-full rounded-xl bg-greenx py-6 hover:bg-greenx-light"><Camera className="me-2 h-5 w-5" /> فتح الكاميرا لمسح الرمز</Button>
                <div className="my-4 flex items-center gap-3 text-xs text-slate-300"><div className="h-px flex-1 bg-slate-100" /> أو أدخل الرمز يدويًا <div className="h-px flex-1 bg-slate-100" /></div>
                <div className="flex gap-2">
                  <Input data-testid="manual-token" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="رمز الحضور" className="rounded-lg" />
                  <Button data-testid="manual-submit" onClick={() => handleToken(manual)} disabled={!manual} className="rounded-lg bg-navy hover:bg-navy-light">تأكيد</Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="animate-scale-in rounded-2xl border border-slate-100 bg-white p-6 soft-shadow" data-testid="attendance-details">
            <div className="mb-4 flex items-center gap-2 text-greenx"><CheckCircle2 className="h-6 w-6" /><span className="font-heading font-bold">تأكيد بيانات الحضور</span></div>
            <div className="space-y-2 text-sm">
              <Row icon={BookOpen} label="البرنامج" value={details.program_name} />
              <Row icon={GraduationCap} label="المحاضرة" value={details.title} />
              <Row icon={User} label="المحاضر" value={details.lecturer_name} />
              <Row icon={Clock} label="الوقت" value={`${details.date} — ${details.start_time} إلى ${details.end_time}`} />
              <Row icon={Building2} label="الوحدة السكنية" value={details.unit_name || "—"} />
              <Row icon={Users} label="الفئة" value={categoryLabel[details.gender]} />
            </div>
            <div className="mt-5">
              <Label className="flex items-center gap-2 text-slate-600"><User className="h-4 w-4" /> اكتب اسمك لتأكيد الحضور</Label>
              <Input data-testid="attendance-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="الاسم الكامل" className="mt-2 rounded-xl" />
            </div>
            <div className="mt-6 flex gap-3">
              <Button data-testid="confirm-attendance-btn" onClick={confirm} disabled={saving || !name.trim()} className="flex-1 rounded-xl bg-greenx py-6 hover:bg-greenx-light">{saving ? "جارٍ الحفظ..." : "حفظ الحضور"}</Button>
              <Button onClick={() => { setDetails(null); setName(""); }} variant="outline" className="rounded-xl">إلغاء</Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5">
      <span className="flex items-center gap-2 text-slate-500"><Icon className="h-4 w-4" /> {label}</span>
      <span className="font-semibold text-navy">{value}</span>
    </div>
  );
}
